<script setup lang="ts">
import { useAuth0 } from '@auth0/auth0-vue';
import { computed, provide, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useProjectStore } from '/@features/project';
import { loadColors } from '/@stores/colors';
import { useTenantStore } from '/@stores/tenant';
import { useUserStore } from '/@stores/user';

import AppNav from './AppNav.vue';
import AppShortcuts from './AppShortcuts.vue';
import AppToasts from './AppToasts.vue';
import { useTimerStore } from '/@stores/timer';
import { registerTokenGetter } from '/@utilities/http';

const navOpen = ref(false);
const navCollapsed = ref(JSON.parse(localStorage.getItem('nav/collapsed')) ?? false);

const isTest = ref(checkIsTest(true));
const isUAT = ref(checkIsUAT());
const isBeta = ref(checkIsBeta());

function getBannerText() {
  if (isBeta.value) return 'Dokflyt Beta';
  else if (isUAT.value) return 'Dokflyt UAT-miljø';
  else return 'Dokflyt testmiljø';
}

function toggleNav(value = !navOpen.value) {
  navOpen.value = value;
}

function checkIsTest(local = false) {
  const href = window.location.href;
  return href.includes('.test') || (local ? href.includes('localhost') : false);
}

function checkIsUAT() {
  return window.location.href.includes('.uat');
}

function checkIsBeta() {
  return window.location.href.includes('.beta');
}

provide('toggle-app-nav', toggleNav);

const route = useRoute();

watch(
  () => route.path,
  () => {
    toggleNav();
    navOpen.value = false;
  },
);

watch(navCollapsed, (value) => localStorage.setItem('nav/collapsed', String(value)));

const userStore = useUserStore();
const tenantStore = useTenantStore();
const projectStore = useProjectStore();
const timerStore = useTimerStore();

const shouldShowBanner = computed(() => isTest.value || isUAT.value || isBeta.value);

const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0();

// getAccessTokenSilently requires a component context (Vue inject), so it must be
// registered here rather than directly in the HTTP module.
registerTokenGetter(() => getAccessTokenSilently().catch(() => undefined));

function initialize() {
  if (!isAuthenticated.value) return;

  Promise.all([
    userStore.loadUser().then(() => timerStore.loadRejectedRegistrations(userStore.user.id)),
    tenantStore.loadTenant().then(() => tenantStore.loadModules()),
    projectStore.loadProjectTypes(),
    loadColors(),
  ]);
}

watch(
  [isAuthenticated, isAuthLoading],
  ([auth, loading]) => {
    if (auth && !loading) {
      initialize();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div v-if="shouldShowBanner" class="fixed-banner" :class="{ 'fixed-banner--beta': isBeta }">
    <df-icon code="f071" />
    {{ getBannerText() }}
  </div>
  <div class="app-container">
    <div class="app" :class="{ 'app--nav-open': navOpen, 'app--nav-collapsed': navCollapsed }">
      <div class="app__nav-container" v-on:click.self="navOpen = false">
        <app-nav class="app__nav" v-model:collapsed="navCollapsed" />
      </div>

      <main>
        <!-- <div v-if="isLoading">Loading…</div> -->
        <router-view v-if="!isAuthLoading" />
      </main>

      <app-shortcuts class="app__shortcuts" />
    </div>

    <app-toasts />
  </div>
</template>

<style scoped>
.app-container {
  position: relative;
  z-index: 0;
  min-height: 100dvh;
  display: grid;
}

.app {
  display: grid;

  @media (min-width: 1000px) {
    grid-template-columns: [nav] 15rem [main] 1fr;
  }

  @media (max-width: 999.5px) {
    grid-template-rows: [main] 1fr [shortcuts] 4rem;
  }
}

.app--nav-collapsed {
  @media (min-width: 1000px) {
    grid-template-columns: [nav] 3.25rem [main] 1fr;
  }
}

.app--nav-open .app__nav-container {
  background-color: var(--color-backdrop);
}

.fixed-banner {
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  color: black;
  text-transform: uppercase;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 10px 0;
  z-index: 10000;
  background-color: hsl(46, 100%, 65%);
}

.fixed-banner--beta {
  background-color: var(--color-danger_text);
  color: white;
}

.app__nav-container {
  max-height: 100vh;
  overflow: auto;
  z-index: 1;

  @media (min-width: 1000px) {
    grid-column: nav;
    position: sticky;
    top: 0;
  }

  @media (max-width: 999.5px) {
    position: fixed;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

@media (max-width: 999.5px) {
  .app__nav-container {
    transform: translateX(-100%);
    transition: transform 0.25s ease-in-out;
  }

  .app--nav-open .app__nav-container {
    transform: translateX(0);
    transition: background-color 0.25s ease-in-out;
  }

  .app__nav {
    transform: translateX(-100%);
    transition: 0.25s ease-in-out;
  }

  .app--nav-open .app__nav {
    transform: translateX(0);
    transition: 0.25s ease-in-out;
  }
}

.app__shortcuts {
  @media (min-width: 1000px) {
    display: none;
  }

  @media (max-width: 999.5px) {
    grid-row: shortcuts;
    position: sticky;
    bottom: 0;
    z-index: 1;
  }
}
</style>
